import { VehicleTelemetry } from '../vehicle/VehiclePhysics';

export class DashboardCluster {
  public canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private width: number = 640;
  private height: number = 160;
  private dpr: number = 2;

  // Smoothed needles for realistic dial physics
  private displaySpeed: number = 0;
  private displayRpm: number = 0;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'hud-dashboard-canvas';
    this.canvas.style.cssText = `
      width: ${this.width}px;
      height: ${this.height}px;
      max-width: 95vw;
      height: auto;
      aspect-ratio: 4 / 1;
      display: block;
      filter: drop-shadow(0 12px 28px rgba(0, 0, 0, 0.75));
      border-radius: 20px;
    `;

    this.dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 2) : 2;
    this.canvas.width = this.width * this.dpr;
    this.canvas.height = this.height * this.dpr;

    this.ctx = this.canvas.getContext('2d')!;
    this.ctx.scale(this.dpr, this.dpr);
  }

  public render(
    telemetry: VehicleTelemetry,
    speedLimitKmh: number,
    blinkerState: string,
    timeString: string,
    deltaTime: number
  ) {
    const ctx = this.ctx;
    const w = this.width;
    const h = this.height;

    // Smooth needle interpolation
    const speed = Math.abs(telemetry.speedKmh);
    const rpm = telemetry.engineRunning && !telemetry.isStalled ? telemetry.rpm : 0;
    this.displaySpeed += (speed - this.displaySpeed) * Math.min(1.0, deltaTime * 14);
    this.displayRpm += (rpm - this.displayRpm) * Math.min(1.0, deltaTime * 14);

    ctx.clearRect(0, 0, w, h);

    // 1. Dashboard Binnacle Glass Background
    const bgGrad = ctx.createLinearGradient(0, 0, 0, h);
    bgGrad.addColorStop(0, 'rgba(15, 23, 42, 0.92)');
    bgGrad.addColorStop(1, 'rgba(2, 6, 23, 0.96)');

    ctx.save();
    ctx.beginPath();
    ctx.roundRect(0, 0, w, h, 20);
    ctx.fillStyle = bgGrad;
    ctx.fill();

    // Subtle metallic cyan rim
    ctx.strokeStyle = 'rgba(56, 189, 248, 0.25)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();

    // 2. Render Left Gauge: Tachometer (0 - 7000 RPM)
    this.renderTachometer(ctx, 110, 84, 62, this.displayRpm, telemetry.gear, telemetry.isStalled);

    // 3. Render Center Multifunction Computer (Bordcomputer)
    this.renderCenterComputer(ctx, w / 2, 84, speedLimitKmh, timeString, telemetry, blinkerState);

    // 4. Render Right Gauge: Speedometer (0 - 220 km/h)
    this.renderSpeedometer(ctx, w - 110, 84, 62, this.displaySpeed, speedLimitKmh);
  }

  private renderTachometer(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    rpm: number,
    gear: string,
    isStalled: boolean
  ) {
    ctx.save();

    // Dial background disc
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(30, 41, 59, 0.45)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(71, 85, 105, 0.5)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Sweep: 0 RPM at 135° (2.356 rad) -> 7000 RPM at 405° (7.068 rad) = 270° total sweep
    const startAngle = Math.PI * 0.75; // 135 deg
    const totalSweep = Math.PI * 1.5;  // 270 deg

    // Base track
    ctx.beginPath();
    ctx.arc(cx, cy, radius - 6, startAngle, startAngle + totalSweep);
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.2)';
    ctx.lineWidth = 4;
    ctx.stroke();

    // Redline zone (6000 - 7000 RPM -> last 1/7th of sweep)
    const redlineStart = startAngle + totalSweep * (6 / 7);
    ctx.beginPath();
    ctx.arc(cx, cy, radius - 6, redlineStart, startAngle + totalSweep);
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 5;
    ctx.stroke();

    // Ticks & Numbers (0 to 7)
    ctx.fillStyle = '#94a3b8';
    ctx.font = '700 9px "Chakra Petch", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (let i = 0; i <= 7; i++) {
      const angle = startAngle + (i / 7) * totalSweep;
      const isRed = i >= 6;
      const tickInner = radius - (i % 1 === 0 ? 12 : 8);
      const tickOuter = radius - 4;

      const x1 = cx + Math.cos(angle) * tickInner;
      const y1 = cy + Math.sin(angle) * tickInner;
      const x2 = cx + Math.cos(angle) * tickOuter;
      const y2 = cy + Math.sin(angle) * tickOuter;

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.strokeStyle = isRed ? '#ef4444' : '#cbd5e1';
      ctx.lineWidth = isRed ? 2 : 1.5;
      ctx.stroke();

      // Number
      const numR = radius - 20;
      const nx = cx + Math.cos(angle) * numR;
      const ny = cy + Math.sin(angle) * numR;
      ctx.fillStyle = isRed ? '#f87171' : '#cbd5e1';
      ctx.fillText(`${i}`, nx, ny);
    }

    // Needle
    const rpmClamped = Math.max(0, Math.min(7000, rpm));
    const needleAngle = startAngle + (rpmClamped / 7000) * totalSweep;

    this.drawNeedle(ctx, cx, cy, radius - 10, needleAngle, '#ef4444');

    // Center Hub & Gear Readout
    ctx.beginPath();
    ctx.arc(cx, cy, 22, 0, Math.PI * 2);
    ctx.fillStyle = '#0f172a';
    ctx.fill();
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Gear text
    ctx.fillStyle = isStalled ? '#ef4444' : '#f59e0b';
    ctx.font = '800 15px "Chakra Petch", sans-serif';
    ctx.fillText(isStalled ? 'STOP' : gear, cx, cy - 2);

    ctx.fillStyle = '#64748b';
    ctx.font = '700 8px "Chakra Petch", sans-serif';
    ctx.fillText('RPM x1000', cx, cy + 10);

    ctx.restore();
  }

  private renderSpeedometer(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    radius: number,
    speedKmh: number,
    speedLimitKmh: number
  ) {
    ctx.save();

    // Dial background disc
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(30, 41, 59, 0.45)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(71, 85, 105, 0.5)';
    ctx.lineWidth = 2;
    ctx.stroke();

    const startAngle = Math.PI * 0.75; // 135 deg
    const totalSweep = Math.PI * 1.5;  // 270 deg
    const maxSpeed = 220;

    // Base track
    ctx.beginPath();
    ctx.arc(cx, cy, radius - 6, startAngle, startAngle + totalSweep);
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.2)';
    ctx.lineWidth = 4;
    ctx.stroke();

    // Speed ticks (0, 20, 30, 50, 70, 100, 130, 160, 200, 220)
    const tickValues = [0, 20, 30, 50, 70, 100, 130, 160, 200, 220];
    ctx.font = '700 8px "Chakra Petch", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    tickValues.forEach((val) => {
      const angle = startAngle + (val / maxSpeed) * totalSweep;
      const isStVOLimit = val === 30 || val === 50;
      const tickInner = radius - (isStVOLimit ? 14 : 9);
      const tickOuter = radius - 4;

      const x1 = cx + Math.cos(angle) * tickInner;
      const y1 = cy + Math.sin(angle) * tickInner;
      const x2 = cx + Math.cos(angle) * tickOuter;
      const y2 = cy + Math.sin(angle) * tickOuter;

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.strokeStyle = isStVOLimit ? '#ef4444' : '#cbd5e1';
      ctx.lineWidth = isStVOLimit ? 2.2 : 1.2;
      ctx.stroke();

      if (val % 40 === 0 || val === 30 || val === 50 || val === 130) {
        const numR = radius - 20;
        const nx = cx + Math.cos(angle) * numR;
        const ny = cy + Math.sin(angle) * numR;
        ctx.fillStyle = isStVOLimit ? '#f87171' : '#cbd5e1';
        ctx.fillText(`${val}`, nx, ny);
      }
    });

    // Speed Needle
    const clampedSpeed = Math.max(0, Math.min(maxSpeed, speedKmh));
    const needleAngle = startAngle + (clampedSpeed / maxSpeed) * totalSweep;

    const isOverSpeed = speedKmh > speedLimitKmh + 3;
    this.drawNeedle(ctx, cx, cy, radius - 10, needleAngle, isOverSpeed ? '#ef4444' : '#38bdf8');

    // Center Hub & Digital Speed Readout
    ctx.beginPath();
    ctx.arc(cx, cy, 22, 0, Math.PI * 2);
    ctx.fillStyle = '#0f172a';
    ctx.fill();
    ctx.strokeStyle = isOverSpeed ? '#ef4444' : '#38bdf8';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Speed value
    ctx.fillStyle = isOverSpeed ? '#ef4444' : '#38bdf8';
    ctx.font = '800 16px "Chakra Petch", sans-serif';
    ctx.fillText(`${Math.round(speedKmh)}`, cx, cy - 2);

    ctx.fillStyle = '#94a3b8';
    ctx.font = '700 8px "Chakra Petch", sans-serif';
    ctx.fillText('KM/H', cx, cy + 10);

    ctx.restore();
  }

  private renderCenterComputer(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    speedLimitKmh: number,
    timeString: string,
    telemetry: VehicleTelemetry,
    blinkerState: string
  ) {
    ctx.save();

    // On-board computer screen panel
    const boxW = 160;
    const boxH = 110;
    const boxX = cx - boxW / 2;
    const boxY = cy - boxH / 2;

    ctx.beginPath();
    ctx.roundRect(boxX, boxY, boxW, boxH, 12);
    ctx.fillStyle = 'rgba(15, 23, 42, 0.7)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(51, 65, 85, 0.8)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // 1. Top Indicator Lamps: [⬅️] [🔒] [🅿️] [🔑] [➡️]
    const isBlinkOn = blinkerState !== 'off' && Math.floor(Date.now() / 380) % 2 === 0;

    // Blinker Left
    ctx.fillStyle = (blinkerState === 'left' || blinkerState === 'hazard') && isBlinkOn ? '#22c55e' : 'rgba(71, 85, 105, 0.35)';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('⬅️', boxX + 16, boxY + 22);

    // Seatbelt (Red when unbuckled, Green when buckled)
    ctx.fillStyle = telemetry.seatbeltFastened ? '#22c55e' : '#ef4444';
    ctx.fillText('🔒', boxX + 48, boxY + 22);

    // Handbrake (Red when active, Green when released)
    ctx.fillStyle = telemetry.handbrake ? '#ef4444' : '#22c55e';
    ctx.fillText('🅿️', boxX + 80, boxY + 22);

    // Ignition Key (Green when running, Amber when off/stalled)
    ctx.fillStyle = telemetry.engineRunning && !telemetry.isStalled ? '#22c55e' : '#f59e0b';
    ctx.fillText('🔑', boxX + 112, boxY + 22);

    // Blinker Right
    ctx.fillStyle = (blinkerState === 'right' || blinkerState === 'hazard') && isBlinkOn ? '#22c55e' : 'rgba(71, 85, 105, 0.35)';
    ctx.fillText('➡️', boxX + 144, boxY + 22);

    // 2. Middle Row: StVO Speed Limit Sign (German Zeichen 274) & Live Time
    // Speed limit sign
    const signX = boxX + 38;
    const signY = boxY + 62;
    const signR = 18;

    ctx.beginPath();
    ctx.arc(signX, signY, signR, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.strokeStyle = '#dc2626';
    ctx.lineWidth = 4;
    ctx.stroke();

    ctx.fillStyle = '#0f172a';
    ctx.font = '800 13px "Chakra Petch", sans-serif';
    ctx.fillText(`${speedLimitKmh}`, signX, signY + 4);

    // Live Clock & Trip distance
    ctx.textAlign = 'left';
    ctx.fillStyle = '#38bdf8';
    ctx.font = '800 16px "Chakra Petch", sans-serif';
    ctx.fillText(timeString || '12:00', boxX + 70, boxY + 58);

    ctx.fillStyle = '#94a3b8';
    ctx.font = '600 10px "Chakra Petch", sans-serif';
    const distKm = (telemetry.distanceTraveledMeters / 1000).toFixed(2);
    ctx.fillText(`TRIP: ${distKm} km`, boxX + 70, boxY + 74);

    // 3. Bottom Row: StVO Status Pill
    ctx.textAlign = 'center';
    const statusText = telemetry.isStalled
      ? '⚠️ MOTOR ABGEWÜRGT'
      : telemetry.handbrake
      ? '🅿️ HANDBREMSE ANGEZOGEN'
      : !telemetry.seatbeltFastened && Math.abs(telemetry.speedKmh) > 5
      ? '🚨 GURT ANLEGEN!'
      : '🇩🇪 StVO PRÜFUNGSMODUS';

    const statusColor = telemetry.isStalled || (!telemetry.seatbeltFastened && Math.abs(telemetry.speedKmh) > 5)
      ? '#ef4444'
      : telemetry.handbrake
      ? '#f59e0b'
      : '#22c55e';

    ctx.fillStyle = statusColor;
    ctx.font = '700 9px "Chakra Petch", sans-serif';
    ctx.fillText(statusText, cx, boxY + 98);

    ctx.restore();
  }

  private drawNeedle(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    length: number,
    angle: number,
    color: string
  ) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);

    // Glowing needle shaft
    ctx.beginPath();
    ctx.moveTo(-4, 0);
    ctx.lineTo(length, 0);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.shadowColor = color;
    ctx.shadowBlur = 6;
    ctx.stroke();

    // Center pivot point
    ctx.beginPath();
    ctx.arc(0, 0, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#f8fafc';
    ctx.fill();

    ctx.restore();
  }
}
